import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import {
  LayoutDashboard,
  CheckSquare,
  Calendar,
  Home,
  Users,
  Settings,
  LogOut,
  Menu,
  X,
  ShoppingCart,
} from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";

const navItems = [
  { path: "/", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/tasks", icon: CheckSquare, label: "Tasks" },
  { path: "/calendar", icon: Calendar, label: "Calendar" },
  { path: "/shopping", icon: ShoppingCart, label: "Shopping" },
  { path: "/automation", icon: Home, label: "Automation" },
  { path: "/members", icon: Users, label: "Members" },
  { path: "/settings", icon: Settings, label: "Settings" },
];

export default function MainLayout() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [appName, setAppName] = useState("FamilySync");

  const fetchAppName = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setAppName(res.data.app_name || "FamilySync");
    } catch (error) {
      console.error("Failed to fetch app name:", error);
    }
  }, []);

  useEffect(() => {
    fetchAppName();
    
    // Listen for app name changes
    const handleAppNameChange = (e) => {
      setAppName(e.detail);
    };
    window.addEventListener('appNameChanged', handleAppNameChange);
    
    return () => {
      window.removeEventListener('appNameChanged', handleAppNameChange);
    };
  }, [fetchAppName]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Split app name for styling (first word normal, rest highlighted)
  const getStyledAppName = () => {
    const parts = appName.split(/(?=[A-Z])|[\s-_]/);
    if (parts.length >= 2) {
      return { first: parts[0], rest: parts.slice(1).join('') };
    }
    // If single word, split in half or show as-is
    if (appName.length > 4) {
      const mid = Math.ceil(appName.length / 2);
      return { first: appName.slice(0, mid), rest: appName.slice(mid) };
    }
    return { first: appName, rest: '' };
  };

  const styledName = getStyledAppName();

  return (
    <div className="min-h-screen bg-[#020617] flex">
      {/* Desktop Sidebar - hidden on mobile, flex on lg screens */}
      <aside className="desktop-sidebar hidden lg:flex flex-col w-64 fixed h-full z-20 bg-slate-900/80 backdrop-blur-md border-r border-white/5">
        <div className="p-6">
          <h1 className="text-2xl font-bold text-white font-['Outfit']" data-testid="app-title">
            {styledName.first}<span className="text-indigo-500">{styledName.rest}</span>
          </h1>
        </div>

        <nav className="flex-1 px-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === "/"}
              className={({ isActive }) =>
                `sidebar-item ${isActive ? "active" : ""}`
              }
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <item.icon size={20} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-slate-800">
          <button
            onClick={handleLogout}
            className="sidebar-item w-full text-slate-400 hover:text-red-400"
            data-testid="logout-button"
          >
            <LogOut size={20} />
            <span>Lock</span>
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-slate-900/95 backdrop-blur-md border-b border-slate-800">
        <div className="flex items-center justify-between p-4">
          <h1 className="text-xl font-bold text-white font-['Outfit']">
            {styledName.first}<span className="text-indigo-500">{styledName.rest}</span>
          </h1>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 text-slate-400 hover:text-white"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/50"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Menu */}
      <div
        className={`lg:hidden fixed top-16 left-0 right-0 z-50 bg-slate-900/95 backdrop-blur-md border-b border-slate-800 transition-all duration-300 ease-in-out ${
          mobileMenuOpen ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-4 pointer-events-none"
        }`}
      >
        <nav className="p-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === "/"}
              onClick={() => setMobileMenuOpen(false)}
              className={({ isActive }) =>
                `sidebar-item ${isActive ? "active" : ""}`
              }
              data-testid={`mobile-nav-${item.label.toLowerCase()}`}
            >
              <item.icon size={20} />
              <span>{item.label}</span>
            </NavLink>
          ))}
          <button
            onClick={handleLogout}
            className="sidebar-item w-full text-slate-400 hover:text-red-400"
            data-testid="mobile-logout-button"
          >
            <LogOut size={20} />
            <span>Lock</span>
          </button>
        </nav>
      </div>

      {/* Main Content */}
      <main className="flex-1 lg:ml-64 pt-16 lg:pt-0">
        <div className="p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
